import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../utils/auth';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import {
  Search,
  Coins,
  Crown,
  Microscope,
  Loader2,
  Plus,
  Minus,
  RefreshCw,
  UserPlus,
  Copy,
  Check,
  X
} from 'lucide-react';

const AdminUsers = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tokenInputs, setTokenInputs] = useState({});
  const [busyUserId, setBusyUserId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedAccount, setGeneratedAccount] = useState(null); // { email, password }
  const [copiedField, setCopiedField] = useState(null);
  const [permissionList, setPermissionList] = useState([]); // [{key, label}]

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error(t('pl.admin.users.loadFail'));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(query) ||
        (u.name || '').toLowerCase().includes(query) ||
        String(u.id).includes(query)
    );
  }, [users, search]);

  const handleTokenInputChange = (userId, value) => {
    setTokenInputs((prev) => ({ ...prev, [userId]: value }));
  };

  const applyTokenDelta = async (targetUser, sign) => {
    const rawValue = tokenInputs[targetUser.id];
    const parsed = parseInt(rawValue, 10);
    if (!rawValue || Number.isNaN(parsed) || parsed <= 0) {
      toast.error(t('pl.admin.users.needPositive'));
      return;
    }

    const amount = sign * parsed;
    setBusyUserId(targetUser.id);
    try {
      const result = await adminAPI.addTokens(targetUser.id, amount);
      setUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? result.user : u))
      );
      setTokenInputs((prev) => ({ ...prev, [targetUser.id]: '' }));
      toast.success(
        sign > 0
          ? t('pl.admin.users.credited', { n: parsed, email: targetUser.email })
          : t('pl.admin.users.debited', { n: parsed, email: targetUser.email })
      );
    } catch (error) {
      console.error('Failed to update tokens:', error);
      toast.error(error.response?.data?.detail || t('pl.admin.users.tokensFail'));
    } finally {
      setBusyUserId(null);
    }
  };

  useEffect(() => {
    adminAPI.listPermissions().then(setPermissionList).catch(() => setPermissionList([]));
  }, []);

  const togglePermission = async (targetUser, key) => {
    const current = targetUser.permissions || [];
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    setBusyUserId(targetUser.id);
    try {
      const result = await adminAPI.setPermissions(targetUser.id, next);
      setUsers((prev) => prev.map((u) => (u.id === targetUser.id ? result.user : u)));
      toast.success(t('pl.admin.users.permsUpdated', { email: targetUser.email }));
    } catch (error) {
      console.error('Failed to update permissions:', error);
      toast.error(error.response?.data?.detail || t('pl.admin.users.permsFail'));
    } finally {
      setBusyUserId(null);
    }
  };

  const togglePetrovichAccess = async (targetUser) => {
    setBusyUserId(targetUser.id);
    try {
      const result = await adminAPI.setPetrovichAccess(
        targetUser.id,
        !targetUser.petrovich_access
      );
      setUsers((prev) =>
        prev.map((u) => (u.id === targetUser.id ? result.user : u))
      );
      toast.success(
        result.user.petrovich_access
          ? t('pl.admin.users.petroGranted', { email: targetUser.email })
          : t('pl.admin.users.petroRevoked', { email: targetUser.email })
      );
    } catch (error) {
      console.error('Failed to update petrovich access:', error);
      toast.error(error.response?.data?.detail || t('pl.admin.users.accessFail'));
    } finally {
      setBusyUserId(null);
    }
  };

  const handleGenerateAccount = async () => {
    setGenerating(true);
    try {
      const result = await adminAPI.generateUser();
      setUsers((prev) => [result.user, ...prev]);
      setGeneratedAccount({ email: result.email, password: result.password });
      toast.success(t('pl.admin.users.created', { email: result.email }));
    } catch (error) {
      console.error('Failed to generate account:', error);
      toast.error(error.response?.data?.detail || t('pl.admin.users.createFail'));
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error(t('pl.admin.users.copyFail'));
    }
  };

  const closeGeneratedAccountModal = () => {
    // Пароль нигде не хранится — после закрытия окна получить его снова нельзя.
    setGeneratedAccount(null);
    setCopiedField(null);
  };

  return (
    <div data-testid="admin-users">
      {generatedAccount && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="vintage-border bg-[#F5F1E8] max-w-md w-full p-6 relative">
            <button
              onClick={closeGeneratedAccountModal}
              className="absolute top-3 right-3 text-gray-500 hover:text-black"
              aria-label={t('pl.common.close')}
            >
              <X size={20} />
            </button>

            <h2
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('pl.admin.users.modalTitle')}
            </h2>
            <p className="text-sm text-red-700 mb-5">
              {t('pl.admin.users.passwordWarn')}
            </p>

            <label className="block text-sm text-gray-600 mb-1">Email</label>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={generatedAccount.email}
                className="flex-1 px-3 py-2 border-2 border-black bg-white font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(generatedAccount.email, 'email')}
                className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
                title={t('pl.admin.users.copyEmail')}
              >
                {copiedField === 'email' ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>

            <label className="block text-sm text-gray-600 mb-1">{t('pl.admin.users.password')}</label>
            <div className="flex items-center gap-2 mb-6">
              <input
                type="text"
                readOnly
                value={generatedAccount.password}
                className="flex-1 px-3 py-2 border-2 border-black bg-white font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(generatedAccount.password, 'password')}
                className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors"
                title={t('pl.admin.users.copyPassword')}
              >
                {copiedField === 'password' ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>

            <button
              onClick={closeGeneratedAccountModal}
              className="w-full engraved-button engraved-button-red"
            >
              {t('pl.admin.users.done')}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end mb-4"><button onClick={loadUsers} disabled={loading} className="flex items-center gap-2 text-sm hover:text-[#C41E3A]"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} />{t('pl.admin.users.refresh')}</button></div>

      <main>
        <div className="vintage-border bg-[#F5F1E8] p-6 mb-8">
          <p className="text-gray-700">
            <Trans i18nKey="pl.admin.users.intro" values={{ email: user?.email }}
              components={{ b: <span className="font-semibold" />, c: <code /> }} />
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('pl.admin.users.search')}
              className="w-full pl-10 pr-3 py-2 border-2 border-black bg-white focus:outline-none focus:border-[#C41E3A]"
            />
          </div>

          <button
            onClick={handleGenerateAccount}
            disabled={generating}
            className="engraved-button engraved-button-red flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="admin-create-account-btn"
          >
            {generating ? <Loader2 size={18} className="animate-spin" /> : <UserPlus size={18} />}
            {t('pl.admin.users.create')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[#C41E3A]" />
          </div>
        ) : (
          <div className="vintage-border bg-[#F5F1E8] overflow-x-auto">
            <table className="w-full text-left min-w-[1100px]">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="p-4">ID</th>
                  <th className="p-4">{t('pl.admin.users.colUser')}</th>
                  <th className="p-4">{t('pl.admin.users.colRole')}</th>
                  <th className="p-4">{t('pl.admin.users.colTokens')}</th>
                  <th className="p-4">{t('pl.admin.users.colDelta')}</th>
                  <th className="p-4">{t('pl.admin.users.colPetro')}</th>
                  <th className="p-4">{t('pl.admin.users.colPerms')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-gray-300 align-top">
                    <td className="p-4 text-gray-600">{u.id}</td>
                    <td className="p-4">
                      <div className="font-semibold">{u.name}</div>
                      <div className="text-sm text-gray-600">{u.email}</div>
                      {u.is_generated_account && (
                        <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-gray-200 border border-gray-400 rounded-full text-gray-700">
                          {t('pl.admin.users.generated')}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {u.is_admin ? (
                        <span className="inline-flex items-center gap-1 text-yellow-800 font-semibold">
                          <Crown size={16} /> {t('pl.admin.users.roleAdmin')}
                        </span>
                      ) : (
                        <span className="text-gray-600">{t('pl.admin.users.roleUser')}</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="inline-flex items-center gap-1 font-semibold">
                        <Coins size={16} className="text-[#C41E3A]" />
                        {u.is_admin ? '∞' : u.tokens}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={tokenInputs[u.id] || ''}
                          onChange={(e) => handleTokenInputChange(u.id, e.target.value)}
                          placeholder="0"
                          className="w-20 px-2 py-1 border-2 border-black bg-white focus:outline-none focus:border-[#C41E3A]"
                          disabled={busyUserId === u.id}
                        />
                        <button
                          onClick={() => applyTokenDelta(u, 1)}
                          disabled={busyUserId === u.id}
                          className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
                          title={t('pl.admin.users.credit')}
                        >
                          <Plus size={16} />
                        </button>
                        <button
                          onClick={() => applyTokenDelta(u, -1)}
                          disabled={busyUserId === u.id}
                          className="p-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
                          title={t('pl.admin.users.debit')}
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => togglePetrovichAccess(u)}
                        disabled={busyUserId === u.id}
                        className={`inline-flex items-center gap-2 px-3 py-2 border-2 border-black transition-colors disabled:opacity-50 ${
                          u.petrovich_access
                            ? 'bg-[#C41E3A] text-white'
                            : 'bg-white hover:bg-black hover:text-white'
                        }`}
                      >
                        <Microscope size={16} />
                        {u.petrovich_access ? t('pl.admin.users.petroOpen') : t('pl.admin.users.petroNone')}
                      </button>
                    </td>
                    <td className="p-4">
                      {u.is_admin ? (
                        <span className="text-sm text-gray-600">{t('pl.admin.users.fullAccess')}</span>
                      ) : (
                        <div className="space-y-1">
                          {permissionList.map((perm) => (
                            <label key={perm.key} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(u.permissions || []).includes(perm.key)}
                                disabled={busyUserId === u.id}
                                onChange={() => togglePermission(u, perm.key)}
                              />
                              {t(`pl.admin.perm.${perm.key}`, { defaultValue: perm.label })}
                            </label>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}

                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      {t('pl.admin.users.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminUsers;
